import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import { Dialog } from 'primereact/dialog'

import { ProgressSpinnerTemplate } from '../../../../../../../components/ProgressTemplate'
import { useToastContext } from '../../../../../../../contexts'
import { EInvoiceDto } from '../../../../../../../data/models'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../../utils/helper/validateFormikFields'
import {
  containsDisallowedCharacters,
  getDisallowedCharacters,
} from '../../../../../../../utils/regex'
import { useConfigContext } from '../../../../../../ui-config'
import {
  requestUploadEInvoiceAttachment,
  useEInvoiceById,
} from '../../../../../data'
import { UploadAttachmentDialog } from './UploadAttachmentDialog'

interface FormValues {
  attachment?: File
  description?: string
}

type FormFieldName = 'attachment' | 'description'

type Props = {
  invoiceId: EInvoiceDto['id']
  processId: string
}

export const Upload: React.FC<Props> = ({ invoiceId, processId }) => {
  const { t } = useTranslation('eInvoiceDetails')
  const [visible, setVisible] = useState(false)

  const { uiConfig: config } = useConfigContext()

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const { data, mutate } = useEInvoiceById(invoiceId!)
  const [isUploadFileVisible, setIsUploadFileVisible] = useState(true)

  // ************************************************************
  // Formik
  // ************************************************************
  function hasSameName(filename: File) {
    const filesWithSameName =
      data?.Attachments?.filter(
        attachment => attachment.FileName === filename.name,
      ) || []

    return filesWithSameName.length > 0
  }

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      attachment: undefined,
      description: undefined,
    },
    validate: formData => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'attachment',
          config: config.eInvoice.additionalDocuments.attachment,
        },
        {
          fieldName: 'description',
          config: config.eInvoice.additionalDocuments.description,
        },
      ]

      validateFormikFields(fieldsToValidate, formData, errors)

      // Attachment has same name
      if (formData.attachment && hasSameName(formData.attachment!)) {
        errors.attachment = t(
          'stepper.additionalDocuments.table.upload.dialog.form.attachment.validation.fileHasSameName',
        )
      }

      // Check filename for special characters
      const fileName = formData.attachment?.name
      const fileNameWithoutExtension = fileName?.slice(
        0,
        fileName.lastIndexOf('.'),
      )
      if (containsDisallowedCharacters(fileNameWithoutExtension)) {
        errors.attachment = t(
          'stepper.additionalDocuments.table.upload.dialog.form.attachment.validation.attachmentContainsDisallowedCharacters',
          {
            disallowedCharacters: getDisallowedCharacters(
              fileNameWithoutExtension,
            ),
          },
        )
      }

      if (
        formData.attachment &&
        formData.attachment.type !== 'application/pdf'
      ) {
        errors.attachment = t(
          'stepper.additionalDocuments.table.upload.dialog.form.attachment.validation.wrongFiletype',
        )
      }

      return errors
    },
    onSubmit: async formData => {
      await submitForm(formData.attachment!, formData.description!)
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  // submitForm
  const submitForm = async (file: File, description: string) => {
    setIsRequestRunning(true)

    setVisible(false)

    try {
      await requestUploadEInvoiceAttachment(invoiceId!, processId, {
        attachment: file,
        description,
      })

      mutate()

      showToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: t(
          'stepper.additionalDocuments.table.upload.dialog.upload.messages.success.summary',
        ),
        detail: t(
          'stepper.additionalDocuments.table.upload.dialog.upload.messages.success.detail',
          {
            name: file.name,
          },
        ),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t(
          'stepper.additionalDocuments.table.upload.dialog.upload.messages.failure.summary',
        ),
        detail: errorMessage,
      })
    } finally {
      formik.resetForm()
      setIsUploadFileVisible(true)
      setIsRequestRunning(false)
    }
  }

  const handleClose = () => {
    formik.resetForm()
    setIsUploadFileVisible(true)
    setVisible(false)
  }

  return (
    <>
      <UploadAttachmentDialog
        formik={formik}
        isRequestRunning={isRequestRunning}
        isFormFieldValid={isFormFieldValid}
        getFormErrorMessage={getFormErrorMessage}
        handleClose={handleClose}
        visible={visible}
        setVisible={setVisible}
        isUploadFileVisible={isUploadFileVisible}
      />

      <Dialog
        visible={isRequestRunning}
        modal
        closable={false}
        showHeader={false}
        onHide={() => {}}
        className="loading-dialog"
        style={{ width: '300px', background: 'transparent', boxShadow: 'none' }}
        contentStyle={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '3rem',
          background: 'transparent',
          border: 'none',
        }}
      >
        <ProgressSpinnerTemplate />
      </Dialog>
    </>
  )
}

export default Upload
